import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Secondary ("pair") color switcher — auditions the data-accent hue that
 * pairs with the primary. Sets `data-pair` on <html>; the CSS in
 * index.css swaps --color-accent2, which the CEFR ramp and headline
 * numbers read. Mirrors lib/accentPalette.tsx (and the theme provider):
 * synchronous first read + a no-flash inline script in index.html.
 * "amber" is the default and clears the attribute.
 */

const STORAGE_KEY = "lingua.pair";

export type PairColor = "amber" | "coral" | "emerald" | "cyan";

const DEFAULT_PAIR: PairColor = "amber";

export const PAIR_COLORS: {
  value: PairColor;
  label: string;
  swatch: string;
}[] = [
  { value: "amber", label: "Amber", swatch: "#b45309" },
  { value: "coral", label: "Coral", swatch: "#d6492f" },
  { value: "emerald", label: "Emerald", swatch: "#059669" },
  { value: "cyan", label: "Cyan", swatch: "#0891b2" },
];

function isPairColor(v: string): v is PairColor {
  return PAIR_COLORS.some((p) => p.value === v);
}

export function readStoredPair(): PairColor {
  if (typeof window === "undefined") return DEFAULT_PAIR;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isPairColor(raw)) return raw;
  } catch {
    // restricted storage — fall through
  }
  return DEFAULT_PAIR;
}

function applyPair(p: PairColor) {
  if (typeof document === "undefined") return;
  if (p === DEFAULT_PAIR) {
    delete document.documentElement.dataset.pair;
  } else {
    document.documentElement.dataset.pair = p;
  }
}

type PairValue = {
  pair: PairColor;
  setPair: (p: PairColor) => void;
};

const PairContext = createContext<PairValue | null>(null);

export function PairColorProvider({ children }: { children: ReactNode }) {
  const [pair, setPairState] = useState<PairColor>(() => readStoredPair());

  const setPair = useCallback((p: PairColor) => {
    setPairState(p);
    applyPair(p);
    try {
      window.localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // session-only if storage is blocked
    }
  }, []);

  useEffect(() => {
    applyPair(pair);
  }, [pair]);

  const value = useMemo(() => ({ pair, setPair }), [pair, setPair]);

  return <PairContext.Provider value={value}>{children}</PairContext.Provider>;
}

export function usePairColor(): PairValue {
  const ctx = useContext(PairContext);
  if (!ctx) {
    throw new Error("usePairColor must be called inside <PairColorProvider>");
  }
  return ctx;
}
